import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import { Plus } from "lucide-react";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "~/components/ui/button";
import {
  CategoryOption,
  CategorySelectionModal,
} from "./CategorySelectionModal";
import { MAX_SECTIONS, TemplateSection } from "./helpers";
import { SectionItem } from "./SectionItem";

interface DraggableSectionsProps {
  sections: TemplateSection[];
  onChange: (sections: TemplateSection[]) => void;
  showError?: boolean;
  parsingError?: boolean;
  availableCategories?: CategoryOption[];
}

export interface EditableSection extends TemplateSection {
  id: string;
  sectionType?: "snapshot" | "docList";
  config?: {
    excludedCategoryNames?: string[];
  };
}

export function DraggableSections({
  sections,
  onChange,
  showError = false,
  parsingError = false,
  availableCategories = [],
}: DraggableSectionsProps) {
  // Convert sections to editable format with unique IDs computed once per mount
  const initialEditableSections = React.useMemo(() => {
    const now = Date.now();
    return sections.map((section, index) => ({
      ...section,
      id: `section-${index}-${now}`,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editableSections, setEditableSections] = useState<EditableSection[]>(
    () => initialEditableSections
  );

  // Track collapsed state (initially all collapsed). Initialize once from initial sections.
  const [collapsedPrompts, setCollapsedPrompts] = useState<Set<string>>(
    () => new Set(initialEditableSections.map((s) => s.id))
  );

  const [isCategorySelectionOpen, setIsCategorySelectionOpen] = useState(false);
  const [pendingDocListSectionId, setPendingDocListSectionId] = useState<
    string | null
  >(null);

  const handleSectionsChange = (updatedSections: EditableSection[]) => {
    const newSections = updatedSections.map((section, index) => ({
      title: section.title,
      purpose: section.purpose,
      prompt: section.prompt,
      order: index,
      sectionType: section.sectionType || "snapshot",
      config: section.config,
    }));
    onChange(newSections);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newSections = Array.from(editableSections);
    const [reorderedSection] = newSections.splice(result.source.index, 1);
    newSections.splice(result.destination.index, 0, reorderedSection);

    setEditableSections(newSections);
    handleSectionsChange(newSections);
  };

  const addSection = () => {
    const newSection: EditableSection = {
      id: `section-${Date.now()}`,
      title: "",
      purpose: "",
      prompt: "",
      order: editableSections.length,
      sectionType: "snapshot",
    };
    const newSections = [...editableSections, newSection];
    setEditableSections(newSections);
    setCollapsedPrompts((prev) => new Set([...prev, newSection.id]));
    handleSectionsChange(newSections);
  };

  const handleAddDocList = () => {
    const newSection: EditableSection = {
      id: `section-${Date.now()}`,
      title: "Document List",
      purpose: "List all documents in the case organized by category",
      prompt: "N/A - Programmatically generated",
      order: editableSections.length,
      sectionType: "docList",
      config: { excludedCategoryNames: [] },
    };

    const newSections = [...editableSections, newSection];
    setEditableSections(newSections);
    setCollapsedPrompts((prev) => new Set([...prev, newSection.id]));

    if (availableCategories.length > 0) {
      setPendingDocListSectionId(newSection.id);
      setIsCategorySelectionOpen(true);
    } else {
      handleSectionsChange(newSections);
    }
  };

  const handleCategorySelectionConfirm = (excludedNames: string[]) => {
    if (pendingDocListSectionId) {
      const updatedSections = editableSections.map((section) =>
        section.id === pendingDocListSectionId
          ? { ...section, config: { excludedCategoryNames: excludedNames } }
          : section
      );
      setEditableSections(updatedSections);
      handleSectionsChange(updatedSections);
    }
    setPendingDocListSectionId(null);
  };

  const removeSection = (id: string) => {
    const newSections = editableSections.filter((section) => section.id !== id);
    setEditableSections(newSections);
    // Clean up collapsed state when section is removed
    setCollapsedPrompts((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    handleSectionsChange(newSections);
  };

  const updateSection = (
    id: string,
    field: keyof Omit<EditableSection, "id" | "order">,
    value: string
  ) => {
    const newSections = editableSections.map((section) =>
      section.id === id ? { ...section, [field]: value } : section
    );
    setEditableSections(newSections);
    handleSectionsChange(newSections);
  };

  const handleCategoryToggle = (sectionId: string, categoryName: string) => {
    const updatedSections = editableSections.map((section) => {
      if (section.id === sectionId && section.sectionType === "docList") {
        const excludedNames = section.config?.excludedCategoryNames || [];
        const newExcludedNames = excludedNames.includes(categoryName)
          ? excludedNames.filter((name) => name !== categoryName)
          : [...excludedNames, categoryName];
        return {
          ...section,
          config: {
            ...section.config,
            excludedCategoryNames: newExcludedNames,
          },
        };
      }
      return section;
    });
    setEditableSections(updatedSections);
    handleSectionsChange(updatedSections);
  };

  const handleSelectAllCategories = (sectionId: string) => {
    const updatedSections = editableSections.map((section) => {
      if (section.id === sectionId && section.sectionType === "docList") {
        return {
          ...section,
          config: { ...section.config, excludedCategoryNames: [] },
        };
      }
      return section;
    });
    setEditableSections(updatedSections);
    handleSectionsChange(updatedSections);
  };

  const handleClearAllCategories = (sectionId: string) => {
    const updatedSections = editableSections.map((section) => {
      if (section.id === sectionId && section.sectionType === "docList") {
        const allCategoryNames = availableCategories.map((cat) => cat.name);
        return {
          ...section,
          config: {
            ...section.config,
            excludedCategoryNames: allCategoryNames,
          },
        };
      }
      return section;
    });
    setEditableSections(updatedSections);
    handleSectionsChange(updatedSections);
  };

  const togglePromptCollapse = (sectionId: string) => {
    setCollapsedPrompts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const hasEmptyFields = editableSections.some((section) => {
    if (section.sectionType === "docList") {
      return !section.title;
    }
    return !section.title || !section.purpose || !section.prompt;
  });

  return (
    <div className="space-y-4">
      {editableSections.length === 0 && showError && (
        <p className="text-sm text-destructive">
          <FormattedMessage
            id="template.sectionsRequired"
            defaultMessage="At least one section is required"
          />
        </p>
      )}

      {parsingError && hasEmptyFields && (
        <p className="text-sm text-destructive">
          <FormattedMessage
            id="template.sectionsInvalid"
            defaultMessage="All sections must have a title, purpose, and prompt"
          />
        </p>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sections" type="section">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-4"
            >
              {editableSections.map((section, index) => (
                <Draggable
                  key={section.id}
                  draggableId={section.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <SectionItem
                      section={section}
                      provided={provided}
                      isDragging={snapshot.isDragging}
                      onUpdate={updateSection}
                      onRemove={removeSection}
                      onToggleCollapse={togglePromptCollapse}
                      isCollapsed={collapsedPrompts.has(section.id)}
                      isRemoveDisabled={editableSections.length === 1}
                      showError={showError}
                      parsingError={parsingError}
                      availableCategories={availableCategories}
                      onCategoryToggle={handleCategoryToggle}
                      onSelectAllCategories={handleSelectAllCategories}
                      onClearAllCategories={handleClearAllCategories}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          <FormattedMessage
            id="template.dragInstruction"
            defaultMessage="Drag sections by their handle to reorder them."
          />
        </p>
        {editableSections.length < MAX_SECTIONS && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addSection}
            >
              <Plus className="w-4 h-4 mr-2" />
              <FormattedMessage
                id="template.addSection"
                defaultMessage="Add Section"
              />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddDocList}
            >
              <Plus className="w-4 h-4 mr-2" />
              <FormattedMessage
                id="template.addDocList"
                defaultMessage="Add Doc List"
              />
            </Button>
          </div>
        )}
      </div>

      <CategorySelectionModal
        isOpen={isCategorySelectionOpen}
        onClose={() => {
          setIsCategorySelectionOpen(false);
          setPendingDocListSectionId(null);
        }}
        categories={availableCategories}
        initialExcludedNames={
          pendingDocListSectionId
            ? editableSections.find((s) => s.id === pendingDocListSectionId)
                ?.config?.excludedCategoryNames || []
            : []
        }
        onConfirm={handleCategorySelectionConfirm}
      />
    </div>
  );
}
